import { CommonModule, DOCUMENT } from '@angular/common';
import { HttpClientModule } from '@angular/common/http';
import { ChangeDetectorRef, Component, OnInit, Inject } from '@angular/core';
import { ApiCallService } from '../../Services/ApiCallService';   // same path as in timelessmonk.ts
import { Title, Meta } from '@angular/platform-browser';

export interface TeamProfile {
  ProfileID: number;
  Slug: string;
  Name: string;
  Designation: string | null;
  Highlights: string | null;
  Vision: string | null;
  Headline: string | null;
  Description: string | null;
  MoreHeadline: string | null;
  MoreDescription: string | null;
  Image: string | null;
  ShowOnTeamPage: boolean;
  SortOrder: number;
}

export interface TeamContent {
  ContentID: number;
  SectionKey: string;     // HEADER | VISION
  Title: string | null;
  Description: string | null;
  Image: string | null;
}

@Component({
  selector: 'app-ourteam',
  standalone: true,
  imports: [CommonModule, HttpClientModule],
  templateUrl: './ourteam.html',
  styleUrl: './ourteam.css',
  host: { ngSkipHydration: 'true' },
})
export class Ourteam implements OnInit {
  imageBaseUrl = 'https://crm-api.redmonkwellness.com';

  header: TeamContent | null = null;
  vision: TeamContent | null = null;
  profiles: TeamProfile[] = [];

  constructor(private api: ApiCallService, private cdr: ChangeDetectorRef
    , private title: Title,
    private meta: Meta,
    @Inject(DOCUMENT) private document: Document,
  ) { }

  ngOnInit(): void {

    this.setSeo();

    this.api.postData('Team', 'GetTeamData', {}).subscribe({
      next: (res: any) => {
        if (!(res?.success ?? res?.Success)) return;

        const contents: TeamContent[] = res.Content || [];
        this.header = contents.find(c => c.SectionKey === 'HEADER') || null;
        this.vision = contents.find(c => c.SectionKey === 'VISION') || null;
        this.profiles = (res.Profiles || []).filter((p: TeamProfile) => p.ShowOnTeamPage);
        this.cdr.detectChanges();
      },
      error: (err) => console.error(err),
    });
  }

  // one item per line (Highlights)
  lines(text: string | null | undefined): string[] {
    return (text || '').split(/\r?\n/).map(l => l.trim()).filter(l => l);
  }

  // blank line = new paragraph
  paragraphs(text: string | null | undefined): string[] {
    return (text || '').split(/\r?\n\s*\r?\n/).map(p => p.trim()).filter(p => p);
  }

  // /assets/... stays as is, uploaded images get the API url
  contentImg(path: string | null | undefined): string {
    if (!path) return '';
    if (/^https?:\/\//i.test(path) || path.startsWith('data:') || path.startsWith('/assets')) return path;
    return this.imageBaseUrl.replace(/\/+$/, '') + '/' + path.replace(/^\/+/, '');
  }



  private setSeo(): void {
    const pageTitle =
      'Red Monk Wellness Team | IV Therapy & Wellness Experts';

    const description =
      'Meet the experts behind Red Monk Wellness, including specialists in sports medicine, regenerative wellness, functional medicine, cognitive performance and aesthetic care.';

    const canonicalUrl =
      'https://www.redmonkwellness.com/ourteam';

    const ogImage =
      'https://www.redmonkwellness.com/assets/images/Ashim-Matthan-rhinomight.jpg';

    // TITLE
    this.title.setTitle(pageTitle);

    // BASIC META
    this.meta.updateTag({
      name: 'description',
      content: description
    });

    this.meta.updateTag({
      name: 'robots',
      content:
        'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1'
    });

    this.meta.updateTag({
      name: 'viewport',
      content: 'width=device-width, initial-scale=1'
    });

    // OPEN GRAPH
    this.meta.updateTag({
      property: 'og:title',
      content: pageTitle
    });

    this.meta.updateTag({
      property: 'og:description',
      content: description
    });

    this.meta.updateTag({
      property: 'og:url',
      content: canonicalUrl
    });

    this.meta.updateTag({
      property: 'og:type',
      content: 'website'
    });

    this.meta.updateTag({
      property: 'og:image',
      content: ogImage
    });

    this.meta.updateTag({
      property: 'og:image:alt',
      content:
        'Ashim Matthan and the Red Monk Wellness team of wellness experts'
    });

    this.meta.updateTag({
      property: 'og:image:type',
      content: 'image/jpeg'
    });

    this.meta.updateTag({
      property: 'og:locale',
      content: 'en_IN'
    });

    this.meta.updateTag({
      property: 'og:site_name',
      content: 'Red Monk Wellness'
    });

    // TWITTER / X
    this.meta.updateTag({
      name: 'twitter:card',
      content: 'summary_large_image'
    });

    this.meta.updateTag({
      name: 'twitter:title',
      content: pageTitle
    });

    this.meta.updateTag({
      name: 'twitter:description',
      content: description
    });

    this.meta.updateTag({
      name: 'twitter:image',
      content: ogImage
    });

    this.meta.updateTag({
      name: 'twitter:image:alt',
      content:
        'Ashim Matthan and the Red Monk Wellness team of wellness experts'
    });

    // CANONICAL URL
    let canonical = this.document.querySelector(
      'link[rel="canonical"]'
    ) as HTMLLinkElement | null;

    if (!canonical) {
      canonical = this.document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      this.document.head.appendChild(canonical);
    }

    canonical.setAttribute('href', canonicalUrl);

    // SITEMAP
    let sitemap = this.document.querySelector(
      'link[rel="sitemap"]'
    ) as HTMLLinkElement | null;

    if (!sitemap) {
      sitemap = this.document.createElement('link');
      sitemap.setAttribute('rel', 'sitemap');
      sitemap.setAttribute('type', 'application/xml');
      sitemap.setAttribute('title', 'Sitemap');
      this.document.head.appendChild(sitemap);
    }

    sitemap.setAttribute(
      'href',
      'https://www.redmonkwellness.com/sitemap.xml'
    );

    // ORGANIZATION SCHEMA
    const organizationSchema = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      '@id': 'https://www.redmonkwellness.com/#organization',
      name: 'Red Monk Wellness',
      url: 'https://www.redmonkwellness.com/',
      logo:
        'https://www.redmonkwellness.com/assets/images/Redmonk-Wellness-Logo-w.png',
      sameAs: [
        'https://www.linkedin.com/company/red-monk-wellness',
        'https://www.facebook.com/redmonkwellness'
      ]
    };

    // WEBPAGE SCHEMA
    const webpageSchema = {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      '@id': `${canonicalUrl}#webpage`,
      url: canonicalUrl,
      name: pageTitle,
      headline: 'Our Team of Wellness & Medical Experts',
      description,
      isPartOf: {
        '@id': 'https://www.redmonkwellness.com/#website'
      },
      inLanguage: 'en-IN',
      breadcrumb: {
        '@id': `${canonicalUrl}#breadcrumb`
      }
    };

    // BREADCRUMB SCHEMA
    const breadcrumbSchema = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      '@id': `${canonicalUrl}#breadcrumb`,
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: 'https://www.redmonkwellness.com/home'
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Our Team',
          item: canonicalUrl
        }
      ]
    };

    // ADD / UPDATE JSON-LD SCHEMAS
    const schemas = [
      {
        id: 'organization-schema',
        data: organizationSchema
      },
      {
        id: 'webpage-schema',
        data: webpageSchema
      },
      {
        id: 'breadcrumb-schema',
        data: breadcrumbSchema
      }
    ];

    schemas.forEach(({ id, data }) => {
      let script = this.document.getElementById(
        id
      ) as HTMLScriptElement | null;

      if (!script) {
        script = this.document.createElement('script');
        script.type = 'application/ld+json';
        script.id = id;
        this.document.head.appendChild(script);
      }

      script.textContent = JSON.stringify(data);
    });
  }



}